/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

/**
 * Browser-side Google Geolocation API (considerIp uses the shopper's public IP, not MRT egress).
 * Key: public site preference GOOGLE_GEOLOCATION_BROWSER_API_KEY.
 * Never use the server credential here. This REST API is separate from Maps JavaScript;
 * validate supported application restrictions before enabling a browser key.
 *
 * @see https://developers.google.com/maps/documentation/geolocation/overview
 */

const GEOLOCATE_URL = 'https://www.googleapis.com/geolocation/v1/geolocate';

/** Align with king-living server geolocate middleware allowed body keys. */
const ALLOWED_BODY_KEYS = new Set([
  'carrier',
  'cellTowers',
  'considerIp',
  'homeMobileCountryCode',
  'homeMobileNetworkCode',
  'homeMobileSubscriberId',
  'radioType',
  'wifiAccessPoints',
]);

const DEFAULT_TIMEOUT_MS = 5000;

/**
 * @param {Object} [body]
 * @returns {Object}
 */
function pickAllowedGeolocationBody(body) {
  if (!body || typeof body !== 'object' || Array.isArray(body)) {
    return {};
  }

  const out = {};

  for (const key of ALLOWED_BODY_KEYS) {
    if (Object.prototype.hasOwnProperty.call(body, key)) {
      out[key] = body[key];
    }
  }

  return out;
}

/**
 * POST Google Geolocation API from the browser.
 *
 * @param {string} apiKey - GOOGLE_GEOLOCATION_BROWSER_API_KEY (site preference)
 * @param {Object} [body] - e.g. { considerIp: true } (default)
 * @param {Object} [options]
 * @param {number} [options.timeoutMs=5000]
 * @returns {Promise<{ location: { lat: number, lng: number }, accuracy?: number }>}
 */
export async function clientGoogleGeolocate(apiKey, body = {considerIp: true}, options = {}) {
  if (typeof window === 'undefined') {
    throw new Error('clientGoogleGeolocate must run in the browser');
  }

  const key = typeof apiKey === 'string' ? apiKey.trim() : '';

  if (!key) {
    throw new Error('Missing Google Geolocation API key');
  }

  const timeoutMs =
    typeof options.timeoutMs === 'number' && options.timeoutMs > 0 ?
      options.timeoutMs :
      DEFAULT_TIMEOUT_MS;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  const url = `${GEOLOCATE_URL}?key=${encodeURIComponent(key)}`;
  const payload = pickAllowedGeolocationBody(body);

  if (!Object.prototype.hasOwnProperty.call(payload, 'considerIp')) {
    payload.considerIp = true;
  }

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify(payload),
      signal: controller.signal,
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      const message =
        data.error?.message || data.error_message || data.message || `HTTP error ${response.status}`;

      throw new Error(message);
    }

    if (data.error) {
      throw new Error(data.error?.message || String(data.error));
    }

    const loc = data?.location;

    if (!loc || typeof loc.lat !== 'number' || typeof loc.lng !== 'number') {
      throw new Error('Invalid geolocation response: missing location coordinates');
    }

    return {
      location: {lat: loc.lat, lng: loc.lng},
      ...(typeof data.accuracy === 'number' ? {accuracy: data.accuracy} : {}),
    };
  } catch (err) {
    if (err?.name === 'AbortError') {
      throw new Error('Google Geolocation request timed out');
    }

    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
}
